/* eslint-disable max-lines -- workflows is a locked root; Agent Home and Chat rows share this host so kebab + right-click stay in one place. */
import {
  AGENT_HOME_ROW_ACTION,
  AGENT_HOME_THREAD_ROW_HOVER,
  AGENT_HOME_THREAD_ROW_TEXT,
  useIsAgentHomeNav,
} from '@/components/layout/Navigation/nav-v3/agent-home-nav-context';
import { WorkSidebarApprovalHand } from '@/components/layout/Navigation/nav-v3/work-sidebar-approval-hand';
import { NavItem } from '@/components/layout/Navigation/sidebar/nav-item';
import { SidebarNavRowStatusIcon } from '@/components/layout/Navigation/sidebar/sidebar-nav-row-status';
import { TeamsSelection } from '@/components/ui/visbility-settings/teams-selection';
import { cn } from '@/lib/cn';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from '@heidi/design-system/context-menu';
import { SidebarGroup } from '@heidi/design-system/page-shell';
import { TypographyP2 } from '@heidi/design-system/typography';
import { useAtomValue, useSetAtom } from 'jotai';
import { Loader2 } from 'lucide-react';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';

import { useIsWorkflowsEnabled } from '@/features/flags';

import {
  RoutinesSidebarNavEntry as PackageRoutinesSidebarNavEntry,
  RoutineActionsMenu,
  WorkflowsSidebarCollapsibleSection,
  type RoutineActionsHost,
  type WorkflowsSidebarRoutineRowRenderer,
} from '@heidi/agents';
import { focusedWorkflowIdFromSelection, type Workflow } from '@heidi/agents/contracts';
import {
  artifactScopeKey,
  currentWorkflowSelectionAtom,
  latestRunByWorkflowId,
  openArtifactInScopeAtom,
  orderWorkflowsByLatestActivity,
  useAllWorkflowRuns,
  useCachedPendingApprovalRunIds,
  useFetchLatestRunWithProject,
  useResolveOwnedRoutineLanding,
  useRoutineForProject,
  useWorkflows,
  useWorkflowSchedules,
} from '@heidi/agents/data';
import { RoutinesNavSpotlight } from './routines-spotlight';
import { useOpenRoutinesSurface } from './useOpenRoutinesSurface';

// ChatSidebar already imports this module — re-export the lock hook here so
// the page does not grow a Chat-quarantine outbound pair.
// eslint-disable-next-line react-refresh/only-export-components -- hook lives in a sibling .ts
export { useRoutinesSpotlightSidebarLock } from '@heidi/agents/data';

/**
 * App-side host for `@heidi/agents`'s `RoutinesSidebarNavEntry`. The package
 * component only tracks the onboarding-spotlight atoms; the coachmark wrapper
 * itself (`RoutinesNavSpotlight`, which reaches a tracked/analytics button)
 * is app-only, so it is supplied here as a render prop rather than imported
 * inside the package. External prop signature is unchanged so this stays a
 * drop-in for the existing `useChatSidebarWorkBindings` call site.
 */
export function RoutinesSidebarNavEntry({
  isSidebarOpen,
  onRequestOpenSidebar,
  children,
}: {
  isSidebarOpen: boolean;
  onRequestOpenSidebar: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <PackageRoutinesSidebarNavEntry
      isSidebarOpen={isSidebarOpen}
      onRequestOpenSidebar={onRequestOpenSidebar}
      renderSpotlight={(spotlightChildren) => (
        <RoutinesNavSpotlight>{spotlightChildren}</RoutinesNavSpotlight>
      )}
    >
      {children}
    </PackageRoutinesSidebarNavEntry>
  );
}

/** Collapsed list length — keeps Scheduled from crowding Saved / Recent. */
const INITIAL_VISIBLE_ROUTINES = 5;
const EMPTY_ATTENTION_PROJECT_IDS: ReadonlySet<string> = new Set();

/**
 * Chat-sidebar routines list: a single "Scheduled" heading, listing only
 * routines with an active (unpaused) schedule — a paused schedule, an
 * on-demand routine and an event-only routine all stay reachable on the
 * Routines page instead, never here. Scheduled rows that have not run yet
 * still list, ordered by latest activity. Hides when the list is empty.
 * Clicking a routine opens its latest run on the standalone chat page as a
 * chat with the routine card open; a routine with no runs yet falls back to
 * the first-run landing on the Routines page, also with the card open.
 */
export function WorkflowsSidebarSection({
  isSidebarPanelEnabled,
  currentChatId = null,
  attentionProjectIds = EMPTY_ATTENTION_PROJECT_IDS,
}: {
  isSidebarPanelEnabled: boolean;
  /** Active chat/project id — used to highlight the owning routine. */
  currentChatId?: string | null;
  /** Project chats with a pending approval, supplied by the parent sidebar. */
  attentionProjectIds?: ReadonlySet<string>;
}): JSX.Element | null {
  // Same switch NavItem uses, so this section and the Recents rows beside it
  // adopt the Agent Home treatment together or not at all.
  const isAgentHomePresentation = useIsAgentHomeNav();
  const [sectionOpen, setSectionOpen] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const router = useRouter();
  const routineActionsHost = useMemo<RoutineActionsHost>(
    () => ({
      navigate: async (href) => {
        await router.push(href);
      },
      TeamsSelection,
      sidebarActionClassName: AGENT_HOME_ROW_ACTION,
    }),
    [router]
  );
  const isWorkflowsEnabled = useIsWorkflowsEnabled();
  const fetchLatestRun = useFetchLatestRunWithProject();
  const resolveOwnedRoutineLanding = useResolveOwnedRoutineLanding({
    enabled: isWorkflowsEnabled,
  });
  const openRoutines = useOpenRoutinesSurface();
  const openArtifactInScope = useSetAtom(openArtifactInScopeAtom);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const openingRef = useRef(false);
  const currentSelection = useAtomValue(currentWorkflowSelectionAtom);
  const { workflow: routineForProject } = useRoutineForProject(
    currentChatId ?? undefined
  );
  const {
    data: workflowsData,
    isLoading,
    isError: workflowsIsError,
  } = useWorkflows({ creator_type: 'user' });
  const { data: allRunsData } = useAllWorkflowRuns();
  const workflows = useMemo(
    () => workflowsData?.workflows ?? [],
    [workflowsData?.workflows]
  );
  const workflowIds = useMemo(
    () => workflows.map((workflow) => workflow.workflow_id),
    [workflows]
  );
  const {
    data: schedulesData,
    schedules,
    isPending: schedulesPending,
    isError: schedulesIsError,
  } = useWorkflowSchedules(workflowIds);
  // Scheduled-and-active rows only: a paused schedule drops the routine from
  // this list entirely, the same as having no schedule at all — only
  // membership matters here, so this skips the recency sort
  // `filterOwnedWorkflows` applies for its own (unrelated) callers. Keeps
  // never-run scheduled rows, ordered by latest activity.
  const listedWorkflows = useMemo(() => {
    const scheduled: Workflow[] = [];
    workflows.forEach((workflow, index) => {
      const schedule = schedules[index]?.data;
      if (schedule && !schedule.paused) scheduled.push(workflow);
    });
    return orderWorkflowsByLatestActivity(scheduled);
  }, [workflows, schedules]);

  const focusedFromSelection = focusedWorkflowIdFromSelection(currentSelection);
  const focusedFromProject = routineForProject?.workflow_id ?? null;
  const focusedWorkflowId = focusedFromSelection ?? focusedFromProject;

  const visibleWorkflows = useMemo(() => {
    if (showAll || listedWorkflows.length <= INITIAL_VISIBLE_ROUTINES) {
      return listedWorkflows;
    }
    const head = listedWorkflows.slice(0, INITIAL_VISIBLE_ROUTINES);
    // Keep the open routine visible even if it sits past the collapse cut.
    if (
      focusedWorkflowId &&
      !head.some((workflow) => workflow.workflow_id === focusedWorkflowId)
    ) {
      const focused = listedWorkflows.find(
        (workflow) => workflow.workflow_id === focusedWorkflowId
      );
      if (focused) return [...head, focused];
    }
    return head;
  }, [focusedWorkflowId, showAll, listedWorkflows]);

  const hiddenCount = Math.max(0, listedWorkflows.length - visibleWorkflows.length);
  const latestRuns = useMemo(
    () => latestRunByWorkflowId(allRunsData?.runs ?? []),
    [allRunsData?.runs]
  );
  const cachedAttentionRunIds = useCachedPendingApprovalRunIds(
    visibleWorkflows.map((workflow) => latestRuns.get(workflow.workflow_id)?.agent_run_id)
  );
  const attentionWorkflowIds = useMemo(() => {
    const ids = new Set<string>();
    for (const workflow of visibleWorkflows) {
      const run = latestRuns.get(workflow.workflow_id);
      if (
        run &&
        (attentionProjectIds.has(run.project_id) ||
          (run.agent_run_id != null && cachedAttentionRunIds.has(run.agent_run_id)))
      ) {
        ids.add(workflow.workflow_id);
      }
    }
    return ids;
  }, [attentionProjectIds, cachedAttentionRunIds, latestRuns, visibleWorkflows]);

  // Sidebar click = the latest run's chat with the routine card open. Listed
  // rows usually already have an openable run; the first-run fallback covers
  // races, enrichment skew, and never-run scheduled rows.
  const openRoutineRun = useCallback(
    async (workflowId: string) => {
      if (openingRef.current) return;
      openingRef.current = true;
      setOpeningId(workflowId);
      try {
        const landing = await resolveOwnedRoutineLanding(workflowId, {
          openCard: true,
        });
        if (landing.kind === 'run') {
          // The landing already opened the card in that chat's slot.
          await router.push(`/scribe/chat/projects/${landing.projectId}`);
          return;
        }
      } catch {
        // Runs fetch failed — the first-run fallback below still works.
      } finally {
        openingRef.current = false;
        setOpeningId(null);
      }
      // Never ran (or fetch failed): first-run landing on the Routines page,
      // tagged as sidebar so the row stays highlighted, card already open.
      // XXX: named for the first-run pane, which mounts after this navigation; the surface on screen now is the one being left.
      openArtifactInScope({
        scope: artifactScopeKey({ kind: 'routine', workflowId }),
        artifact: { type: 'workflow', workflowId },
      });
      openRoutines(
        { kind: 'workflow', id: workflowId, firstRun: true },
        { origin: 'sidebar' }
      );
    },
    [resolveOwnedRoutineLanding, openArtifactInScope, openRoutines, router]
  );
  const prefetchLatestRun = useCallback(
    (workflowId: string) => {
      void fetchLatestRun(workflowId).catch(() => {});
    },
    [fetchLatestRun]
  );
  // App-only row chrome (`NavItem`, `SidebarNavRowStatusIcon`) for the denser
  // Agent Home presentation — the package only computes each row's meta.
  const renderNavItemRow = useCallback<WorkflowsSidebarRoutineRowRenderer>(
    (workflow, { selected, needsAttention, isOpening }) => (
      <RoutineActionsMenu
        key={workflow.workflow_id}
        workflow={workflow}
        host={routineActionsHost}
        trigger="sidebar"
        renderRow={({ action, contextItems }) => (
          <NavItem
            label={workflow.name}
            icon={
              isOpening ? (
                <Loader2
                  className="size-4 shrink-0 animate-spin text-text-tertiary"
                  aria-label="Opening routine"
                />
              ) : (
                <WorkSidebarApprovalHand needsAttention={needsAttention} />
              )
            }
            onClick={() => void openRoutineRun(workflow.workflow_id)}
            onMouseEnter={() => prefetchLatestRun(workflow.workflow_id)}
            isActive={selected}
            disabled={openingId !== null}
            testId={`sidebar-link-routine-${workflow.workflow_id}`}
            truncateLabel
            className={cn(
              isOpening && 'opacity-60',
              needsAttention ? 'text-text-primary' : AGENT_HOME_THREAD_ROW_TEXT,
              AGENT_HOME_THREAD_ROW_HOVER
            )}
            action={action}
            rowContextMenu={contextItems}
          />
        )}
      />
    ),
    [openRoutineRun, prefetchLatestRun, openingId, routineActionsHost]
  );

  // App-only row chrome for the Chat-sidebar presentation.
  const renderChatRow = useCallback<WorkflowsSidebarRoutineRowRenderer>(
    (workflow, { selected, needsAttention, isOpening }) => (
      <RoutineActionsMenu
        key={workflow.workflow_id}
        workflow={workflow}
        host={routineActionsHost}
        trigger="none"
        renderRow={({ contextItems }) => (
          <ContextMenu>
            <ContextMenuTrigger asChild>
              <button
                type="button"
                disabled={openingId !== null}
                onClick={() => void openRoutineRun(workflow.workflow_id)}
                onPointerEnter={() => prefetchLatestRun(workflow.workflow_id)}
                onFocus={() => prefetchLatestRun(workflow.workflow_id)}
                aria-busy={isOpening || undefined}
                className={cn(
                  'flex h-8 w-full items-center gap-1.5 rounded-md px-2 py-1 text-start disabled:cursor-not-allowed',
                  isSidebarPanelEnabled
                    ? 'text-text-primary hover:bg-background-secondary active:bg-background-secondary-pressed'
                    : 'hover:bg-background-secondary',
                  selected && 'bg-background-secondary',
                  isOpening && 'opacity-60'
                )}
              >
                {isOpening ? (
                  <Loader2
                    className="size-3.5 shrink-0 animate-spin text-text-tertiary"
                    aria-label="Opening routine"
                  />
                ) : (
                  <SidebarNavRowStatusIcon
                    status={needsAttention ? 'needs-attention' : 'rest'}
                  />
                )}
                <TypographyP2 className="min-w-0 flex-1 truncate text-text-primary">
                  {workflow.name}
                </TypographyP2>
              </button>
            </ContextMenuTrigger>
            <ContextMenuContent onClick={(event) => event.stopPropagation()}>
              {contextItems}
            </ContextMenuContent>
          </ContextMenu>
        )}
      />
    ),
    [
      openRoutineRun,
      prefetchLatestRun,
      openingId,
      isSidebarPanelEnabled,
      routineActionsHost,
    ]
  );

  // Recents sits in a SidebarGroup of its own; reusing the primitive rather
  // than restating its p-2 is what keeps the two sections from drifting.
  const wrapSection = (section: JSX.Element): JSX.Element =>
    isAgentHomePresentation ? (
      <SidebarGroup data-testid="agent-home-routines-sections">{section}</SidebarGroup>
    ) : (
      section
    );
  const shellProps = {
    isAgentHomePresentation,
    sectionOpen,
    onSectionOpenChange: setSectionOpen,
  };
  // Both queries must land before the filter is trustworthy — an unfiltered
  // list that then sheds rows is the bug to avoid, so this is a loading row
  // rather than a guess or a hide.
  if (isLoading || schedulesPending)
    return wrapSection(
      <WorkflowsSidebarCollapsibleSection {...shellProps} status="loading" />
    );
  // Initial-load failure only — a failed background refetch with the last
  // good payload still cached must not blank a working list.
  const hasError =
    (workflowsIsError && workflowsData === undefined) ||
    (schedulesIsError && schedulesData === undefined);
  if (hasError)
    return wrapSection(
      <WorkflowsSidebarCollapsibleSection {...shellProps} status="error" />
    );
  // Hides when the list is empty (doc comment above): owning nothing at all
  // and owning only routines with no active schedule both land here.
  // `listedWorkflows` derives from `workflows`, so the first case already
  // implies the second, hence one guard rather than two.
  if (listedWorkflows.length === 0) return null;

  return wrapSection(
    <WorkflowsSidebarCollapsibleSection
      {...shellProps}
      status="list"
      visibleWorkflows={visibleWorkflows}
      focusedWorkflowId={focusedWorkflowId}
      focusedFromProject={focusedFromProject}
      currentChatId={currentChatId}
      attentionWorkflowIds={attentionWorkflowIds}
      openingId={openingId}
      isSidebarPanelEnabled={isSidebarPanelEnabled}
      canToggle={listedWorkflows.length > INITIAL_VISIBLE_ROUTINES}
      showAll={showAll}
      hiddenCount={hiddenCount}
      onToggleShowAll={() => setShowAll((prev) => !prev)}
      renderNavItemRow={renderNavItemRow}
      renderChatRow={renderChatRow}
    />
  );
}
